import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { useState, useEffect } from "react";

// Hero images: generate responsive srcsets via vite-imagetools
import hero1Src from "@/assets/hero-1.jpg?format=webp&quality=75&w=800;1280;1920&as=srcset";
import heroVa1Src from "@/assets/hero-va-1.jpg?format=webp&quality=75&w=800;1280;1920&as=srcset";
import hero2Src from "@/assets/hero-2.jpg?format=webp&quality=75&w=800;1280;1920&as=srcset";
import heroVa2Src from "@/assets/hero-va-2.jpg?format=webp&quality=75&w=800;1280;1920&as=srcset";
import hero3Src from "@/assets/hero-3.jpg?format=webp&quality=75&w=800;1280;1920&as=srcset";
import heroVa3Src from "@/assets/hero-va-3.jpg?format=webp&quality=75&w=800;1280;1920&as=srcset";
import hero4Src from "@/assets/hero-4.jpg?format=webp&quality=75&w=800;1280;1920&as=srcset";
import hero5Src from "@/assets/hero-5.jpg?format=webp&quality=75&w=800;1280;1920&as=srcset";
import hero6Src from "@/assets/hero-6.jpg?format=webp&quality=75&w=800;1280;1920&as=srcset";
import hero7Src from "@/assets/hero-7.jpg?format=webp&quality=75&w=800;1280;1920&as=srcset";

// Fallback src (smallest variant) for browsers without srcset support
import hero1Fallback from "@/assets/hero-1.jpg?format=webp&quality=75&w=1280";
import heroVa1Fallback from "@/assets/hero-va-1.jpg?format=webp&quality=75&w=1280";
import hero2Fallback from "@/assets/hero-2.jpg?format=webp&quality=75&w=1280";
import heroVa2Fallback from "@/assets/hero-va-2.jpg?format=webp&quality=75&w=1280";
import hero3Fallback from "@/assets/hero-3.jpg?format=webp&quality=75&w=1280";
import heroVa3Fallback from "@/assets/hero-va-3.jpg?format=webp&quality=75&w=1280";
import hero4Fallback from "@/assets/hero-4.jpg?format=webp&quality=75&w=1280";
import hero5Fallback from "@/assets/hero-5.jpg?format=webp&quality=75&w=1280";
import hero6Fallback from "@/assets/hero-6.jpg?format=webp&quality=75&w=1280";
import hero7Fallback from "@/assets/hero-7.jpg?format=webp&quality=75&w=1280";

const heroImages: Array<{ srcset: string; src: string }> = [
  { srcset: hero1Src, src: hero1Fallback },
  { srcset: heroVa1Src, src: heroVa1Fallback },
  { srcset: hero2Src, src: hero2Fallback },
  { srcset: heroVa2Src, src: heroVa2Fallback },
  { srcset: hero3Src, src: hero3Fallback },
  { srcset: heroVa3Src, src: heroVa3Fallback },
  { srcset: hero4Src, src: hero4Fallback },
  { srcset: hero5Src, src: hero5Fallback },
  { srcset: hero6Src, src: hero6Fallback },
  { srcset: hero7Src, src: hero7Fallback },
];

export const Hero = () => {
  const { t, getLocalizedPath } = useLanguage();
  const [currentImage, setCurrentImage] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImage((prev) => (prev + 1) % heroImages.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden">
      {/* Full screen background images with crossfade */}
      <div className="absolute inset-0">
        {heroImages.map((image, index) => (
          <img
            key={index}
            src={image.src}
            srcSet={image.srcset}
            sizes="100vw"
            alt={t.hero.title}
            width={1920}
            height={1080}
            loading={index === 0 ? "eager" : "lazy"}
            fetchPriority={index === 0 ? "high" : "auto"}
            decoding="async"
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${
              index === currentImage ? "opacity-100" : "opacity-0"
            }`}
          />
        ))}
        <div className="absolute inset-0 bg-charcoal/60" />
      </div>

      {/* Grid overlay */}
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: `linear-gradient(hsl(var(--cream)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--cream)) 1px, transparent 1px)`,
        backgroundSize: '80px 80px'
      }} />

      <div className="container mx-auto px-4 relative z-10 pt-24 pb-16">
        <div className="flex items-center min-h-[80vh]">
          {/* Content */}
          <div className="max-w-xl">
            {/* Main heading */}
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-bold leading-[0.95] mb-6 animate-slide-up text-cream">
              {t.hero.title && <>{t.hero.title}<br /></>}
              <span className="text-gold">{t.hero.titleAccent}</span>
              <br />
              {t.hero.titleEnd}
            </h1>

            {/* Subtitle */}
            <p className="text-lg text-cream/50 max-w-md mb-10 leading-relaxed font-body">
              {t.hero.subtitle}
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row items-start gap-4">
              <Button variant="gold" size="xl" asChild>
                <Link to={getLocalizedPath("/shop")}>
                  {t.hero.shopCollection}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Image indicators */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-1 z-10">
        {heroImages.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentImage(index)}
            className="w-6 h-6 flex items-center justify-center group"
            aria-label={`Go to slide ${index + 1}`}
          >
            <span className={`rounded-full transition-all duration-300 ${
              index === currentImage ? "bg-gold w-6 h-2" : "w-2 h-2 bg-cream/30 group-hover:bg-cream/50"
            }`} />
          </button>
        ))}
      </div>
    </section>
  );
};
